/**
 * Formatação para o operador: pt-BR, America/Sao_Paulo, BRL.
 * Reexporta o que vive nos contratos para que a tela importe de um lugar só.
 */
export { formatCents, formatCentsPlain, parseCents } from '@amai/contracts';
export { formatCep, formatDocument, formatPhone } from '@amai/contracts';

const TIMEZONE = 'America/Sao_Paulo';

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TIMEZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TIMEZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
});

const EMPTY = '—';

/**
 * Dia civil (`2026-09-10`), não instante.
 *
 * A API serializa vencimento, competência, emissão, admissão e nascimento
 * assim — são datas de calendário, sem hora e sem fuso. Passar isso pelo
 * `new Date()` cria meia-noite em UTC, e formatar essa meia-noite em
 * America/Sao_Paulo (UTC−3) devolve o DIA ANTERIOR: `2026-09-10` virava
 * 09/09/2026 em toda tela do painel, o vencimento do contas a pagar
 * inclusive. Dia civil se formata trocando os pedaços de lugar, sem
 * conversão nenhuma.
 */
const DIA_CIVIL = /^(\d{4})-(\d{2})-(\d{2})$/;

function diaCivil(value: string | Date | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const match = DIA_CIVIL.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
}

function parse(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value: string | Date | null | undefined): string {
  const civil = diaCivil(value);
  if (civil) return civil;
  const date = parse(value);
  return date ? dateFormatter.format(date) : EMPTY;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  // Dia civil não tem hora para mostrar — inventar 00:00 seria pior.
  const civil = diaCivil(value);
  if (civil) return civil;
  const date = parse(value);
  return date ? dateTimeFormatter.format(date) : EMPTY;
}

export function formatTime(value: string | Date | null | undefined): string {
  const date = parse(value);
  return date ? timeFormatter.format(date) : EMPTY;
}

/** "há 3 min", "ontem" — para a coluna "quando" da trilha de auditoria. */
export function formatRelative(value: string | Date | null | undefined): string {
  // "há 3 min" não faz sentido para um dia civil.
  const civil = diaCivil(value);
  if (civil) return civil;
  const date = parse(value);
  if (!date) return EMPTY;

  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absolute = Math.abs(diffSeconds);
  const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

  if (absolute < 60) return rtf.format(Math.round(diffSeconds), 'second');
  if (absolute < 3600) return rtf.format(Math.round(diffSeconds / 60), 'minute');
  if (absolute < 86_400) return rtf.format(Math.round(diffSeconds / 3600), 'hour');
  if (absolute < 2_592_000) return rtf.format(Math.round(diffSeconds / 86_400), 'day');
  return formatDate(date);
}

export function blank(value: string | null | undefined): string {
  return value && value.trim() !== '' ? value : EMPTY;
}

/** Iniciais para o avatar da sidebar. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}
